import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Images, Pause, Play, WhatsappLogo } from "@phosphor-icons/react";
import { contactInfo, galleryImages } from "@/data/siteContent";

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState("forward");
  const [progressKey, setProgressKey] = useState(0);
  const activeImage = galleryImages[activeIndex];
  const previousImage = previousIndex !== null ? galleryImages[previousIndex] : null;
  const timeoutRef = useRef(null);
  const previousCleanupRef = useRef(null);
  const startTimeRef = useRef(0);
  const remainingTimeRef = useRef(5000);
  const reducedMotionRef = useRef(false);

  const goToSlide = useCallback((index) => {
    const normalizedIndex = (index + galleryImages.length) % galleryImages.length;
    if (normalizedIndex === activeIndex) return;
    const isLoopForward = activeIndex === galleryImages.length - 1 && normalizedIndex === 0;
    const isMovingForward = normalizedIndex > activeIndex || isLoopForward;
    setTransitionDirection(isMovingForward ? "forward" : "backward");
    setPreviousIndex(activeIndex);
    setActiveIndex(normalizedIndex);
    remainingTimeRef.current = 5000;
    setProgressKey((current) => current + 1);
    if (previousCleanupRef.current) window.clearTimeout(previousCleanupRef.current);
    previousCleanupRef.current = window.setTimeout(() => setPreviousIndex(null), reducedMotionRef.current ? 0 : 820);
  }, [activeIndex]);

  const pauseGallery = () => {
    if (isPaused) return;
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(350, remainingTimeRef.current - elapsed);
    setIsPaused(true);
  };

  const resumeGallery = () => setIsPaused(false);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    galleryImages.forEach((image) => {
      const preload = new Image();
      preload.src = image.src;
    });
  }, []);

  useEffect(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    if (isPaused) return undefined;
    startTimeRef.current = Date.now();
    timeoutRef.current = window.setTimeout(() => {
      setTransitionDirection("forward");
      goToSlide(activeIndex + 1);
    }, remainingTimeRef.current);
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [activeIndex, goToSlide, isPaused]);

  useEffect(() => () => {
    if (previousCleanupRef.current) window.clearTimeout(previousCleanupRef.current);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToSlide(activeIndex + 1);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToSlide(activeIndex - 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, goToSlide]);

  return (
    <section id="galeri" data-testid="gallery-section" className="relative overflow-hidden bg-[#121214] px-6 py-24 lg:px-8 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(249,115,22,0.18),transparent_30%),linear-gradient(135deg,rgba(255,255,255,0.04),transparent_40%)]" />
      <div className="relative mx-auto max-w-7xl">
        <div data-testid="gallery-heading-block" className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <p data-testid="gallery-overline" className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.22em] text-orange-500">
              <Images size={18} weight="duotone" /> Saha Galerisi
            </p>
            <h2 data-testid="gallery-title" className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Gerçek işlerden karelerle asansörlü taşıma gücümüz.</h2>
            <p data-testid="gallery-description" className="mt-5 text-base leading-relaxed text-slate-400 md:text-lg">Fotoğraflar otomatik geçişlidir; görselin üzerine geldiğinizde geçiş durur, oklarla veya küçük görsellerle gezebilirsiniz.</p>
          </div>
          <a
            data-testid="gallery-whatsapp-button"
            href={contactInfo.whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#25D366] px-6 py-4 font-black text-white transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-[#1DA851] hover:shadow-[0_8px_30px_rgba(37,211,102,0.25)]"
          >
            <WhatsappLogo size={22} weight="bold" /> WhatsApp: {contactInfo.phoneDisplay}
          </a>
        </div>

        <div data-testid="gallery-stage" data-stagger-group="gallery-stage" className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div
            data-stagger-item="gallery-main"
            data-testid="gallery-main-card"
            onMouseEnter={pauseGallery}
            onMouseLeave={resumeGallery}
            className="gallery-main-card group relative overflow-hidden rounded-3xl border border-white/15 bg-[#09090A] shadow-2xl"
          >
            <div data-testid="gallery-image-viewport" className="gallery-image-viewport">
              {previousImage && (
                <div key={`previous-${previousImage.id}`} className="gallery-layer-frame gallery-layer-frame-exit">
                  <img
                  key={`previous-${previousImage.id}`}
                  data-testid="gallery-previous-image"
                  src={previousImage.src}
                  alt={`Has Kırıkhan Nakliyat ${previousImage.title} Kırıkhan Hatay`}
                  loading="lazy"
                  style={{ objectFit: "cover" }}
                    className="gallery-layer-image"
                  />
                </div>
              )}
              <div key={`active-${activeImage.id}`} className="gallery-layer-frame gallery-layer-frame-enter">
                <img
                  data-testid="gallery-active-image"
                  src={activeImage.src}
                  alt={`Has Kırıkhan Nakliyat ${activeImage.title} Kırıkhan Hatay`}
                  loading="lazy"
                  style={{ objectFit: "cover" }}
                  className={`gallery-layer-image kenburns-${activeIndex % 2 === 0 ? "left" : "right"}`}
                />
              </div>
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/5 to-black/20" />
            </div>
            <div data-testid="gallery-progress-track" className="absolute bottom-0 left-0 right-0 z-30 h-[3px] bg-white/10">
              <span key={`progress-${progressKey}`} className={`gallery-progress-bar ${isPaused ? "is-paused" : ""}`} />
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-8">
              <div key={`copy-${activeImage.id}`} className="gallery-copy-motion">
                <div data-testid="gallery-slide-counter" className="mb-4 inline-flex rounded-full border border-orange-500/40 bg-orange-500/15 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-orange-300">
                  {String(activeIndex + 1).padStart(2, "0")}/{String(galleryImages.length).padStart(2, "0")}
                </div>
                <h3 data-testid="gallery-active-title" className="font-heading text-2xl font-black text-white md:text-4xl">{activeImage.title}</h3>
                <p data-testid="gallery-active-caption" className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 md:text-base">{activeImage.caption}</p>
              </div>
            </div>

            <div data-testid="gallery-controls" className="gallery-controls absolute right-5 top-5 z-30 flex gap-3">
              <button data-testid="gallery-prev-button" type="button" onClick={() => goToSlide(activeIndex - 1)} className="gallery-nav-button grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-black/80 text-white transition-[transform,opacity,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-orange-500 hover:bg-orange-500 active:scale-95">
                <ArrowLeft size={21} weight="bold" />
              </button>
              <button data-testid="gallery-pause-button" type="button" onClick={() => (isPaused ? resumeGallery() : pauseGallery())} className="gallery-nav-button grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-black/80 text-white transition-[transform,opacity,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-orange-500 hover:bg-orange-500 active:scale-95" aria-label={isPaused ? "Galeriyi oynat" : "Galeriyi duraklat"}>
                {isPaused ? <Play size={21} weight="bold" /> : <Pause size={21} weight="bold" />}
              </button>
              <button data-testid="gallery-next-button" type="button" onClick={() => goToSlide(activeIndex + 1)} className="gallery-nav-button grid h-12 w-12 place-items-center rounded-full border border-white/15 bg-black/80 text-white transition-[transform,opacity,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-orange-500 hover:bg-orange-500 active:scale-95">
                <ArrowRight size={21} weight="bold" />
              </button>
            </div>
          </div>

          <div data-testid="gallery-thumbnail-panel" data-stagger-group="gallery-thumbnails" className="gallery-thumb-scroll flex gap-4 overflow-x-auto pb-2 lg:grid lg:max-h-[680px] lg:grid-cols-1 lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0 lg:pr-2">
            {galleryImages.map((image, index) => (
              <button
                key={image.id}
                data-stagger-item="gallery-thumbnail"
                data-testid={`gallery-thumbnail-${image.id}`}
                type="button"
                onClick={() => goToSlide(index)}
                className={`gallery-thumbnail group relative min-h-28 min-w-[240px] overflow-hidden rounded-2xl border text-left transition-[border-color,transform,opacity,box-shadow] duration-300 ease-out hover:scale-[1.02] hover:border-white/30 hover:opacity-100 lg:min-h-32 lg:min-w-0 ${activeIndex === index ? "gallery-thumb-active border-orange-500 opacity-100 shadow-[0_0_0_1px_rgba(249,115,22,0.55),0_18px_45px_rgba(249,115,22,0.18)]" : "border-white/10 opacity-[0.85]"}`}
              >
                <img data-testid={`gallery-thumbnail-image-${image.id}`} src={image.src} alt={`Has Kırıkhan Nakliyat ${image.title} Kırıkhan Hatay`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent transition-colors duration-500 group-hover:bg-orange-950/20" />
                <span data-testid={`gallery-thumbnail-title-${image.id}`} className="absolute bottom-3 left-4 right-3 font-heading text-sm font-black text-white/90 transition-colors duration-300 group-hover:text-white">{image.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}